import apiSkobo from "@/helpers/api/apiSkobo";
import apiGraph from "@/helpers/api/apiGraph";
import SectionExtension from "@/models/extensions/education/SectionExtension";
import GraphHelper from "@/helpers/utilities/GraphHelper";
import { i18n } from "@/helpers/utilities/I18nHelper";
import FileHelper from "@/helpers/utilities/FileHelper";
import { useSectionsStore } from "@/stores/sections";

/**
 *  Actions for the sections/classes store
 *@module Sections
 * @category Store
 * @subcategory Sections
 * @name Actions
 */

export default {
  /**
   * Add section with sectionId to my sections
   * @param { object } vuex Vuex
   * @param { object } vuex.commit Commit
   * @param { object } vuex.dispatch Dispatch
   * @param { object } section - section to add
   */
  async addSectionToMySections({ commit }, section) {
    commit("addSectionToMySections", section);
    await apiSkobo.put("/me/Section/" + section.sectionId);
    const sectionsStore = useSectionsStore();
    sectionsStore.reloadMySections();
  },

  /**
   * Remove section with sectionId from my sections
   * @param { object } vuex Vuex
   * @param { object } vuex.commit Commit
   * @param { object } vuex.dispatch Dispatch
   * @param {string} sectionId - sectionId of section to remove
   */
  async removeSectionFromMySections({ commit }, sectionId) {
    commit("removeSectionFromMySections", sectionId);
    await apiSkobo.delete("/me/Section/" + sectionId);
    const sectionsStore = useSectionsStore();
    sectionsStore.reloadMySections();
  },

  /**
   * Load drives for a section
   * @param { object } vuex Vuex
   * @param { object } vuex.commit Commit
   * @param { object } vuex.state State
   * @param { object } vuex.getters Getters
   * @param {object} payload Payload
   * @param { string } payload.sectionId Sections id to fetch drives for
   * @param { boolean } payload.forceReload Indicates section drives should be reloaded
   */
  async loadDrives({ commit, state, getters }, { sectionId, forceReload }) {
    if (!forceReload && state.sectionRootDriveMap[sectionId]) {
      return;
    }

    try {
      const section = getters["SECTION"](sectionId);

      if (!section || !section.extSysApiSectionId) {
        return;
      }

      const response = await apiGraph.get(
        `/groups/${section.extSysApiSectionId}/drives?$select=id,name,createdBy,driveType,lastModifiedDateTime,webUrl`
      );

      const filteredDrives = response.data.value.filter(
        d =>
          // Filter on documentLibrary and the drives we do NOT want
          d.driveType === "documentLibrary" && !d.createdBy.user.displayName.startsWith("SharePoint")
      );

      // Translate section drives if a translation exists
      const convertedToDriveItemObject = filteredDrives.map(d => ({
        name: i18n.te("DriveItem." + d.name) ? i18n().t("DriveItem." + d.name) : d.name,
        createdBy: d.createdBy,
        lastModifiedDateTime: d.lastModifiedDateTime,
        extension: FileHelper.getClosedFolderTemplate(),
        folder: true,
        parentReference: {
          driveId: d.id
        },
        webUrl: d.webUrl
      }));

      // Section system drive
      const rootDrive = filteredDrives.find(d => d.createdBy.user.displayName.startsWith("System"));
      commit("loadDrives", { sectionId, rootDrive: rootDrive });

      // Section other drives
      const otherDrives = convertedToDriveItemObject.filter(d => !d.createdBy.user.displayName.startsWith("System"));
      commit("loadSubdrives", { driveId: rootDrive.id, driveItems: otherDrives });
    } catch {
      // eslint-disable-next-line no-empty
    }
  },

  /**
   * Load drives for all my sections (loaddrive foreach section)
   * @action loadAllSectionDrives=none
   * @param { object } vuex Vuex
   * @param { object } vuex.state State
   * @param { object } vuex.dispatch Dispatch
   */
  loadAllSectionDrives({ dispatch, state }) {
    for (let i in state.mySectionsMap) {
      dispatch("loadDrives", { sectionId: i });
    }
  },

  /**
   * Load drives for all my archived sections (loaddrive foreach section)
   * @action loadAllArchivedSectionDrives=none
   * @param { object } vuex Vuex
   * @param { object } vuex.getters Getters
   * @param { object } vuex.dispatch Dispatch
   */
  loadAllArchivedSectionDrives({ dispatch, getters }) {
    for (let i in getters.ARCHIVED_SECTIONS) {
      dispatch("loadDrives", { sectionId: i });
    }
  },

  //
  //
  //
  //
  //
  /**
   * Load section from id if not available in store
   * @action loadSection
   * @param { object } vuex Vuex
   * @param { object } vuex.state State
   * @param { object } vuex.dispatch Dispatch
   * @param { string } sectionId Id of section to load
   * @returns { object } Section
   */
  async loadSection({ state }, sectionId) {
    if (!state.mySectionsMap || !state.mySectionsMap[sectionId]) {
      const sectionsStore = useSectionsStore();
      sectionsStore.loadMySections();
    }
    return state.mySectionsMap[sectionId];
  },

  async loadArchivedSection({ state, dispatch }, sectionId) {
    await dispatch("loadMyArchivedSections");

    const archivedSectionAfterLoad = [
      ...Object.values(state.myArchivedSectionGroups).flatMap(g => Object.values(g.sections))
    ].find(s => s.sectionId === sectionId);
    return archivedSectionAfterLoad;
  },

  /**
   * Load members of section for specified sectionId
   * @action loadMembersForSection=sectionsTeachersMap
   * @param { object } vuex Vuex
   * @param { object } vuex.state State
   * @param { object } vuex.commit Commit
   * @param { object } vuex.dispatch Dispatch
   * @param { string } sectionId sectionId for section
   * @returns { Promise<void>}
   */
  loadMembersForSection({ state, commit, dispatch }, sectionId) {
    return new Promise((resolve, reject) => {
      if (state.sectionsStudentsMap[sectionId] && state.sectionsTeachersMap[sectionId]) {
        resolve();
        return;
      }

      apiSkobo.get(`/Institution/Section/${sectionId}/Members`).then(
        response => {
          if (response.data.students && response.data.students.values) {
            dispatch("loadProfilePicturesFromList", response.data.students.values, { root: true });
          }
          if (response.data.teachers && response.data.teachers.values) {
            dispatch("loadProfilePicturesFromList", response.data.teachers.values, { root: true });
          }

          commit("loadMembersForSection", {
            sectionId: sectionId,
            data: response.data
          });
          resolve();
        },
        error => {
          reject(error);
        }
      );
    });
  },

  /**
   *  Load profile pictures for all sections in list if not already in state
   * @action sectionsLoadProfilePicturesFromList=sectionsProfilePictures
   * @param { object } vuex Vuex
   * @param { object } vuex.state State
   * @param { object } vuex.commit Commit
   * @param { Array } sectionList List of sections to request profile pictures for
   * @returns { void }
   */
  sectionsLoadProfilePicturesFromList({ state, commit }, sectionList) {
    return new Promise((resolve, reject) => {
      const requestForSectionsNotInState = sectionList
        .filter(s => s.extSysApiSectionId && !state.sectionsProfilePictures[s.extSysApiSectionId])
        .map(s => {
          commit("sectionsLoadProfilePicture", { extSysApiSectionId: s.extSysApiSectionId, url: null });
          return SectionExtension.generateGraphBatchRequestFromSection(s);
        });

      if (requestForSectionsNotInState.length === 0) {
        resolve();
        return;
      }

      GraphHelper.makeBatchRequest(requestForSectionsNotInState)
        .then(results => {
          commit("sectionsLoadProfilePicturesFromList", results);
          resolve();
        })
        .catch(error => {
          sectionList.map(s => {
            commit("sectionsRemoveProfilePicture", { extSysApiSectionId: s.extSysApiSectionId });
          });
          reject(error.message);
        });
    });
  },

  /**
   * Load profile picture for section if it does not exist in store - and retry
   * up till 5 times if request fails
   * @action sectionsLoadProfilePicture=sectionsProfilePictures
   * @param { object } vuex Vuex
   * @param { object } vuex.state State
   * @param { object } vuex.dispatch Dispatch
   * @param { string } extSysApiSectionId External sys api id for section to request profile picture for
   * @returns { Promise<object> } Promise with http response
   */
  sectionsLoadProfilePicture({ state, dispatch }, extSysApiSectionId) {
    return new Promise((resolve, reject) => {
      if (!extSysApiSectionId) {
        reject();
        return;
      }

      if (Object.prototype.hasOwnProperty.call(state.sectionsProfilePictures, extSysApiSectionId)) {
        if (state.sectionsProfilePictures[extSysApiSectionId] !== null) {
          resolve(state.sectionsProfilePictures[extSysApiSectionId]);
          return;
        }

        let i = 0;

        const intervaller = setInterval(() => {
          if (state.sectionsProfilePictures[extSysApiSectionId] !== null) {
            resolve(state.sectionsProfilePictures[extSysApiSectionId]);
            return;
          }
          i++;
          if (i > 5) {
            clearInterval(intervaller);
            return dispatch("sectionsDoLoadProfilePicture", extSysApiSectionId).then(response => resolve(response));
          }
        }, 1000);
      } else {
        return dispatch("sectionsDoLoadProfilePicture", extSysApiSectionId).then(response => resolve(response));
      }
    });
  },

  /**
   * Load profile picture for section from graphApi
   * @action sectionsDoLoadProfilePicture=sectionsProfilePictures
   * @param { object } vuex Vuex
   * @param { object } vuex.commit Commit
   * @param { string } extSysApiSectionId External sys api id for section to request profile picture for
   * @returns { Promise<string> } Url
   */
  sectionsDoLoadProfilePicture({ commit }, extSysApiSectionId) {
    return new Promise((resolve, reject) => {
      apiGraph
        .get(`/groups/${extSysApiSectionId}/photos/48x48/$value`, { responseType: "blob" })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));

          commit("sectionsLoadProfilePicture", { extSysApiSectionId: extSysApiSectionId, url: url });
          resolve(url);
        })
        .catch(() => reject());
    });
  }
};
